import { ApolloError, useLazyQuery } from "@apollo/client";
import {
  CircularProgress,
  CssBaseline,
  Fab,
  Grid,
  Typography
} from "@mui/material";
import { MUIDataTableOptions } from "mui-datatables";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { GET_PROTECTION_PLANS } from "../../../../../graphql/protectionPlans/getProtectionPlansQuery";
import { IProtectionPlan } from "../../../../../reducers/protectionPlan/types";
import { IAppState } from "../../../../../store";
import { NuvvenTable } from "../../../../common/NuvvenTable/NuvvenTable";
import { SelectableRows } from "../../../../common/NuvvenTable/types";
import { useSnackBar } from "../../../../common/SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../../common/SnackbarWrapper/SnackbarWrapper";
import {
  formatGraphQLErrorMessage,
  toCurrency
} from "../../../../common/utils";

const ProtectionPlans: React.FC = () => {
  const navigate = useNavigate();
  const snackbar = useSnackBar();
  const [rows, setRows] = useState([]);
  const userState = useSelector((state: IAppState) => state.userReducer);
  const { locale, currency } = userState.currentOrganisation;

  const [protectionPlans, { loading, data }] = useLazyQuery(
    GET_PROTECTION_PLANS,
    {
      fetchPolicy: "network-only",
      onError: (error: ApolloError) => {
        snackbar({
          message: formatGraphQLErrorMessage(error.message),
          variant: SnackBarVariant.ERROR
        });
      }
    }
  );

  const columns: any = [
    {
      label: "Name",
      name: "name"
    },
    {
      label: "Description",
      name: "description"
    },
    {
      label: "Excess",
      name: "excess",
      options: {
        customBodyRender: (value: string) =>
          toCurrency(parseFloat(value), currency, locale)
      }
    }
  ];

  useEffect(() => {
    protectionPlans();
  }, []);

  useEffect(() => {
    if (data && data.protectionPlans) {
      setRows(
        data.protectionPlans.map((protectionPlan: IProtectionPlan) =>
          reshapeAddonsIntoRows(protectionPlan)
        )
      );
    }
  }, [data]);

  const options: MUIDataTableOptions = {
    selectableRows: SelectableRows.NONE,
    onRowClick: (
      rowData: any,
      rowMeta: { dataIndex: number; rowIndex: number }
    ) => {
      const row = data.protectionPlans[rowMeta.dataIndex];
      const protectionPlanId = row.id;
      if (protectionPlanId) {
        navigate(
          `/update-protection-plan?protectionPlanId=${protectionPlanId}`
        );
      }
    }
  };

  const reshapeAddonsIntoRows = (protectionPlan: IProtectionPlan) => {
    return {
      name: protectionPlan.name,
      description: protectionPlan.description,
      excess: protectionPlan.excess
    };
  };
  return (
    <Grid container spacing={2}>
      <CssBaseline />
      <Grid container item xs={6}>
        <Typography variant="h1" color="primary">
          Protection Plans{"  "}
        </Typography>
      </Grid>
      <Grid container item xs={6} justifyContent="flex-end">
        <Fab
          variant="extended"
          size="medium"
          aria-label="Create"
          className="createButton"
          onClick={() => {
            navigate("/new-protection-plan");
          }}
        >
          Create
        </Fab>
      </Grid>
      <Grid container item xs={12}>
        {loading ? (
          <CircularProgress />
        ) : (
          <NuvvenTable
            title={""}
            rows={rows}
            columns={columns}
            setSelection={(item: any) => item}
            options={options}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default ProtectionPlans;
